import React, { useState, useRef, useEffect } from 'react';
import { CopyIcon, LinesIcon, LineBreakIcon, ColorIcon, FontIcon, SearchIcon, ReplaceIcon, UndoIcon, RedoIcon } from './icons/Icons';
import './editorTile.css';

const EditorTile = ({ editor, isActive, onUpdate, onActivate }) => {
    const [showFontSize, setShowFontSize] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showEncoding, setShowEncoding] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [lineNumbers, setLineNumbers] = useState(true);
    const [lineBreak, setLineBreak] = useState(true); 
    const [textColor, setTextColor] = useState('#ffffff');
    const [bgColor, setBgColor] = useState('#1e1e1e');
    const [fontSize, setFontSize] = useState('14px');
    const [encoding, setEncoding] = useState('UTF-8');
    const [lineEnding, setLineEnding] = useState('LF');
    const [isEditingHeading, setIsEditingHeading] = useState(false);
    const [tempHeading, setTempHeading] = useState(editor.heading);
    const [searchMatches, setSearchMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const [highlightedMatches, setHighlightedMatches] = useState([]);
    
    // Undo/Redo States
    const [history, setHistory] = useState([editor.content]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);
    
    const textareaRef = useRef(null);
    const lineNumbersRef = useRef(null);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);

    // Save to history when content changes (except during undo/redo)
    useEffect(() => {
        if (!isUndoRedoAction) {
            const newHistory = history.slice(0, historyIndex + 1);
            newHistory.push(editor.content);
            setHistory(newHistory);
            setHistoryIndex(newHistory.length - 1);
        }
        setIsUndoRedoAction(false);
    }, [editor.content]);

    // Undo function
    const handleUndo = () => {
        if (historyIndex > 0) {
            setIsUndoRedoAction(true);
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            onUpdate({ content: history[newIndex] });
        }
    };

    // Redo function
    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            setIsUndoRedoAction(true);
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            onUpdate({ content: history[newIndex] });
        }
    };

    useEffect(() => {
        if (isActive && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isActive]);

    useEffect(() => {
        const words = editor.content.trim() ? editor.content.trim().split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(editor.content.length);
    }, [editor.content]);

    // Scroll-Sync zwischen line-numbers und textarea (angepasst - kein Punkt in ID)
    useEffect(() => {
        const textarea = textareaRef.current;
        const lineNumbersDiv = lineNumbersRef.current;
        
        if (textarea && lineNumbersDiv && lineNumbers) {
            const handleScroll = () => {
                lineNumbersDiv.scrollTop = textarea.scrollTop;
            };
            textarea.addEventListener('scroll', handleScroll);
            return () => textarea.removeEventListener('scroll', handleScroll);
        }
    }, [lineNumbers]);

    const handleHeadingClick = () => {
        setIsEditingHeading(true);
        setTempHeading(editor.heading);
    };

    const handleHeadingSave = () => {
        if (tempHeading.trim()) {
            onUpdate({ heading: tempHeading.trim() });
        }
        setIsEditingHeading(false);
    };

    const handleHeadingKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleHeadingSave();
        }
    };

    const findAllMatches = () => {
        if (!searchTerm) {
            setSearchMatches([]);
            setHighlightedMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }
        
        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const matches = [];
        let match;
        
        while ((match = regex.exec(editor.content)) !== null) {
            matches.push({ index: match.index, length: searchTerm.length });
        }
        
        setSearchMatches(matches);
        setHighlightedMatches(matches);
        
        if (matches.length > 0) {
            setCurrentMatchIndex(0);
            highlightMatch(matches[0].index, matches[0].length);
            showNotification(`Found ${matches.length} match(es)`);
        } else {
            setCurrentMatchIndex(-1);
            showNotification('No matches found');
        }
    };

    const showNotification = (message) => {
        const notification = document.createElement('div');
        notification.className = 'search-notification';
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 2000);
    };

    const highlightMatch = (index, length) => {
        if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(index, index + length);
        }
    };

    const nextMatch = () => {
        if (searchMatches.length === 0) return;
        const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
        setCurrentMatchIndex(nextIndex);
        highlightMatch(searchMatches[nextIndex].index, searchMatches[nextIndex].length);
    };

    const replaceCurrent = () => {
        if (!replaceTerm || currentMatchIndex === -1) return;
        const match = searchMatches[currentMatchIndex];
        const newContent = editor.content.substring(0, match.index) + 
                          replaceTerm + 
                          editor.content.substring(match.index + searchTerm.length);
        onUpdate({ content: newContent });
        
        setTimeout(() => {
            findAllMatches();
        }, 10);
    };

    const replaceAll = () => {
        if (!searchTerm || !replaceTerm) return;
        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        const newContent = editor.content.replace(regex, replaceTerm);
        onUpdate({ content: newContent });
        setTimeout(() => {
            findAllMatches();
        }, 10);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(editor.content);
            const copyBtn = document.querySelector('.copy-btn');
            if (copyBtn) {
                const originalText = copyBtn.innerHTML;
                copyBtn.innerHTML = '✓ copied!';
                setTimeout(() => {
                    copyBtn.innerHTML = originalText;
                }, 2000);
            }
        } catch (err) {
            console.error('failed to copy:', err);
        }
    };

    const getLineNumbersArray = () => {
        if (!lineNumbers) return [];
        const lines = editor.content.split('\n');
        return lines.map((_, index) => index + 1);
    };

    const handleTextChange = (e) => {
        onUpdate({ content: e.target.value });
    };

    return (
        <div 
            className={`editor-tile ${isActive ? 'active-tile' : ''} ${!editor.isVisible ? 'hidden-tile' : ''}`}
            onClick={onActivate}
            style={{ backgroundColor: bgColor, display: editor.isVisible ? 'block' : 'none' }}
        >
            {/* Heading Section */}
            <div className="tile-header">
                {isEditingHeading ? (
                    <input
                        type="text"
                        value={tempHeading}
                        onChange={(e) => setTempHeading(e.target.value)}
                        onBlur={handleHeadingSave}
                        onKeyPress={handleHeadingKeyPress}
                        className="heading-input"
                        autoFocus
                    />
                ) : (
                    <h3 className="tile-heading" onClick={handleHeadingClick}>
                        {editor.heading}
                    </h3>
                )}
            </div>

            {/* Search and Replace Section */}
            <div className="search-section">
                <div className="search-input-group">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="search-input-group">
                    <ReplaceIcon />
                    <input
                        type="text"
                        placeholder="replace with..."
                        value={replaceTerm}
                        onChange={(e) => setReplaceTerm(e.target.value)}
                        className="replace-input"
                    />
                </div>
                <div className="search-actions">
                    <button onClick={findAllMatches} className="search-action-btn">show all</button>
                    <button onClick={nextMatch} className="search-action-btn" disabled={searchMatches.length === 0}>
                        next ({searchMatches.length})
                    </button>
                    <button onClick={replaceCurrent} className="search-action-btn" disabled={!replaceTerm || currentMatchIndex === -1}>
                        replace
                    </button>
                    <button onClick={replaceAll} className="search-action-btn replace-all-btn" disabled={!replaceTerm}>
                        replace all
                    </button>
                </div>
            </div>

            {/* Textarea with Line Numbers */}
            <div className="textarea-container">
                {lineNumbers && (
                    <div 
                        ref={lineNumbersRef}
                        className="line-numbers" 
                        style={{ fontSize }}
                    >
                        {getLineNumbersArray().map(num => (
                            <div key={num} className="line-number">{num}</div>
                        ))}
                    </div>
                )}
                <textarea
                    ref={textareaRef}
                    value={editor.content}
                    onChange={handleTextChange}
                    className={`editor-textarea ${!lineBreak ? 'no-linebreak' : ''}`}
                    style={{ 
                        fontSize,
                        color: textColor,
                        whiteSpace: lineBreak ? 'pre-wrap' : 'nowrap'
                    }}
                    placeholder="start typing here..."
                />
            </div>

            {/* Word & Character Counter */}
            <div className="counter-section">
                <span>words: {wordCount}</span>
                <span>chars: {charCount}</span>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
                <button 
                    className="toolbar-btn undo-btn"
                    onClick={handleUndo}
                    disabled={historyIndex === 0}
                    title="undo"
                >
                    <UndoIcon /> undo
                </button>
                <button 
                    className="toolbar-btn redo-btn"
                    onClick={handleRedo}
                    disabled={historyIndex === history.length - 1}
                    title="redo"
                >
                    <RedoIcon /> redo
                </button>

                <div className="toolbar-divider"></div>

                <button 
                    className={`toolbar-btn ${lineNumbers ? 'active' : ''}`}
                    onClick={() => setLineNumbers(!lineNumbers)}
                    title="toggle line numbers"
                >
                    <LinesIcon /> lines
                </button>
                
                <button 
                    className={`toolbar-btn ${lineBreak ? 'active' : ''}`}
                    onClick={() => setLineBreak(!lineBreak)}
                    title="toggle line break"
                >
                    <LineBreakIcon /> wrap
                </button>

                <div className="popup-container">
                    <button 
                        className="toolbar-btn"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        title="colors"
                    >
                        <ColorIcon /> color
                    </button>
                    {showColorPicker && (
                        <div className="popup">
                            <div className="popup-item">
                                <label>bg:</label>
                                <input 
                                    type="color" 
                                    value={bgColor} 
                                    onChange={(e) => setBgColor(e.target.value)}
                                />
                            </div>
                            <div className="popup-item">
                                <label>text:</label>
                                <input 
                                    type="color" 
                                    value={textColor} 
                                    onChange={(e) => setTextColor(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="popup-container">
                    <button 
                        className="toolbar-btn"
                        onClick={() => setShowFontSize(!showFontSize)}
                        title="font size"
                    >
                        <FontIcon /> font
                    </button>
                    {showFontSize && (
                        <div className="popup">
                            <input 
                                type="range" 
                                min="10" 
                                max="32" 
                                value={parseInt(fontSize)} 
                                onChange={(e) => setFontSize(`${e.target.value}px`)}
                            />
                            <span className="font-size-value">{fontSize}</span>
                        </div>
                    )}
                </div>

                <div className="popup-container">
                    <button 
                        className="toolbar-btn"
                        onClick={() => setShowEncoding(!showEncoding)}
                        title="encoding"
                    >
                        {encoding}
                    </button>
                    {showEncoding && (
                        <div className="popup encoding-popup">
                            {['UTF-8', 'UTF-16', 'ASCII', 'ISO-8859-1'].map(enc => (
                                <button 
                                    key={enc}
                                    className={`encoding-option ${encoding === enc ? 'active' : ''}`}
                                    onClick={() => { setEncoding(enc); setShowEncoding(false); }}
                                >
                                    {enc}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button 
                    className="toolbar-btn"
                    onClick={() => setLineEnding(lineEnding === 'LF' ? 'CRLF' : 'LF')}
                    title="line ending"
                >
                    {lineEnding}
                </button>

                <button 
                    className="toolbar-btn copy-btn"
                    onClick={handleCopy}
                    title="copy content"
                >
                    <CopyIcon /> copy
                </button>
            </div>
        </div>
    );
};

export default EditorTile;